import type { CampaignRanking, RankingReport } from '@/queries/cpmRanking';
import type { RankingPlatform } from './rankingCompetition';
import { rankingReportStatus } from './rankingPresentation';
import { resolveAccountReliabilityDisplay } from './resolveAccountReliabilityDisplay';
import { isManualFacebookReport } from './manualFacebookReport';

export type RankingBulkAction = 'approve' | 'reject';

/**
 * Examples, finalized competitions, the reviewer's own clips and disqualified
 * clips cannot be decided (the backend refuses a disqualified clip).
 */
export function isRankingReportReadOnly(ranking: CampaignRanking, report: RankingReport, example: boolean): boolean {
  const own = ranking.own_reports.some(own => own.id === report.id)
    || ranking.rows.some(row => row.is_self && row.participant_id === report.participant_id);
  return !ranking.can_review || example || ranking.phase === 'finalized' || own || !!report.disqualified;
}

/** Any canonical clip the reviewer can decide may be selected, pending or already decided. */
export function canBulkSelectRankingClip(ranking: CampaignRanking, report: RankingReport, example: boolean): boolean {
  return !!report.submission_id && !isRankingReportReadOnly(ranking, report, example);
}

/**
 * Bulk approval takes what one click could approve and is not approved yet. It
 * never confirms ownership, so Facebook clips without a connected Page and
 * native clips without a verified owner stay single-clip approvals (the backend
 * skips them as well).
 */
export function canBulkApproveRankingClip(ranking: CampaignRanking, report: RankingReport, example: boolean): boolean {
  return canBulkSelectRankingClip(ranking, report, example) && rankingReportStatus(report) !== 'approved'
    && !isManualFacebookReport(report) && report.ownership_verified === true;
}

/** Rejection needs no ownership proof: any selectable clip that is not rejected yet. */
export function canBulkRejectRankingClip(ranking: CampaignRanking, report: RankingReport, example: boolean): boolean {
  return canBulkSelectRankingClip(ranking, report, example) && rankingReportStatus(report) !== 'rejected';
}

/** The selected clips a bulk action applies to, in selection order. */
export function bulkActionIds(ranking: CampaignRanking, selected: string[], example: boolean, action: RankingBulkAction): string[] {
  const applies = action === 'approve' ? canBulkApproveRankingClip : canBulkRejectRankingClip;
  const reports = new Map(ranking.admin_reports.map(report => [report.submission_id, report]));
  return selected.filter(id => { const report = reports.get(id); return !!report && applies(ranking, report, example); });
}

export function toggleRankingBulkSelection(selected: string[], ids: string[], checked: boolean): string[] {
  if (!checked) return selected.filter(id => !ids.includes(id));
  return [...selected, ...ids.filter((id, index) => !selected.includes(id) && ids.indexOf(id) === index)];
}

/** Keeps only clips still in the latest data that the reviewer can still decide. */
export function pruneRankingBulkSelection(selected: string[], ranking: CampaignRanking, example: boolean): string[] {
  const selectable = new Set(ranking.admin_reports
    .filter(report => canBulkSelectRankingClip(ranking, report, example))
    .map(report => report.submission_id!));
  const kept = selected.filter(id => selectable.has(id));
  return kept.length === selected.length ? selected : kept;
}

export type LowReliabilityGroup = {
  key: string;
  handle: string;
  platform: RankingPlatform;
  reliability: NonNullable<RankingReport['account_reliability']>;
  clips: RankingReport[];
};

/**
 * The given clips whose publishing account has low reliability, grouped by
 * account in selection order, so the reviewer can check them before approving.
 */
export function groupLowReliabilityClips(ranking: CampaignRanking, ids: string[]): LowReliabilityGroup[] {
  const reports = new Map(ranking.admin_reports.map(report => [report.submission_id, report]));
  const groups = new Map<string, LowReliabilityGroup>();
  for (const id of ids) {
    const report = reports.get(id);
    const reliability = report?.account_reliability;
    if (!report || !reliability || resolveAccountReliabilityDisplay(reliability).kind !== 'warning') continue;
    const key = `${report.platform}:${reliability.username ?? report.profile_handle ?? report.participant_id}`;
    const group = groups.get(key) ?? {
      key, platform: report.platform, reliability, clips: [],
      handle: report.profile_handle || (reliability.username ? `@${reliability.username}` : report.participant_id),
    };
    group.clips.push(report);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** A clip review reason is optional; when given it needs at least three letters or numbers. */
export function isValidRankingClipReason(reason: string): boolean {
  return !reason.trim() || (reason.match(/[\p{L}\p{N}]/gu)?.length ?? 0) >= 3;
}
